import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import IntroductionImg3 from "../../../assets/SvgIcons/IntroductionImg3.svg";
import "../JaldiLab.css";
import {
  dimensionStyle,
  generateOrderedList,
  generateUnOrderedList,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceHtml,
  referenceStyle,
  referenceTootKit,
  sectionStyle,
} from "./ChapterList";

import { DashedBorderStyle, DividerBorderStyle } from "./ChapterList";

function CapacityBuilding({ goToSection }) {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: " ‘List of Permanent Committees at Chandigarh High Court’ ",
      link: "https://highcourtchd.gov.in/sub_pages/left_menu/Rules_orders/rti_rules/pdf/VIII_Committee.pdf",
      description:
        "<https://highcourtchd.gov.in/sub_pages/left_menu/Rules_orders/rti_rules/pdf/VIII_Committee.pdf> p. 6. ",
    },
    {
      content:
        "For example, the GSICC directed the installation of automatic sanitary napkin vending machines at the Chandigarh High Court and District Courts under its jurisdiction. ’",
    },
    {
      content: "Rights of Persons with Disabilities, section 39. ",
    },
  ];

  const DesignConsiderations = [
    <>
      Familiarity with the VWDC guidelines and design considerations arising
      from the POCSO Act (
      <span
        onClick={() => goToSection("courtroom", "special-courtrooms")}
        style={sectionStyle}
      >
        PART C, Section 2.1.B
      </span>
      );
    </>,
    <>
      Trauma-informed design considerations for Vulnerable Witnesses (
      <span
        onClick={() => goToSection("vwdc", "vulnerable-witness")}
        style={sectionStyle}
      >
        PART C, Section 2.3.A
      </span>
      );
    </>,
    <>
      Family and child-friendly facilities including provision of crèche/daycare
      facilities, children’s feeding room/nap room, and family washrooms (
      <span
        onClick={() =>
          goToSection(
            "familyChildFriendlyServices",
            "familyChildFriendlyFacilities"
          )
        }
        style={sectionStyle}
      >
        PART C, Section 3.3
      </span>
      ).
    </>,
    <>
      Design of indoor environments for young children including need for quiet
      rooms and sensory rooms, child-friendly signage, etc., (
      <span
        onClick={() => goToSection("quietSensoryRoom", "HeadingQuietRoom")}
        style={sectionStyle}
      >
        PART C, Section 3.6
      </span>
      ); and
    </>,
    <>
      Need for provision for Prayer room (
      <span
        onClick={() => goToSection("prayerRoom", "mainheading")}
        style={sectionStyle}
      >
        PART C, Section 3.5
      </span>
      ).
    </>,
  ];

  const securityArray = [
    "Building awareness of security protocols, including managing threats, violence, terrorism, etc., including training on the technological measures used for managing security;",
    "Building awareness of the fire and emergency evacuation procedures and rules, including accessible requirements for emergency evacuation; ",
    "Sharing of information on nearest available resources such as hospitals, police stations, fire station, ambulance service, etc., needed in times of emergency with all user groups; and ",
    "Practice of regular building evacuation drills for all users.",
  ];
  const infrastructureArray = [
    "New technological advancements in e-Courts management implemented in the Courtrooms and detention facilities, for remote hearings, video conferencing, etc.;",
    "Systems and procedures for e-filing;",
    "Digital document/records management systems using latest technologies, access and privacy policies;",
    <>
      Evidence room management systems and procedures including e-evidence
      management (
      <span
        onClick={() => goToSection("evidenceRoom", "HeadingEvidenceRoom")}
        style={sectionStyle}
      >
        PART C, Section 2.9.D
      </span>
      );
    </>,
    <>
      Policies for sustainable building practices and use of the Building
      Management System (
      <span
        onClick={() =>
          goToSection("siteBuildingManagement", "sustainableBuildingStrategies")
        }
        style={sectionStyle}
      >
        Sections 2.3 & 2.5
      </span>
      );
    </>,
    "Operations of building services equipment and procedures, and site maintenance procedures; ",
    <>
      Management and upkeep of the wayfinding and information systems (
      <span
        onClick={() =>
          goToSection("managementPoliciesStrategies", "dataManagement")
        }
        style={sectionStyle}
      >
        Section 4.2
      </span>
      );{" "}
    </>,
    "Operations of security and emergency response equipment; and",
    "Grievance redressal management.",
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART D - IMPLEMENTATION, MONITORING & EVALUATION
        </h1>{" "}
        <h2 id="mainheading" style={h2style}>
          <span style={{ fontSize: "24px" }}>5. </span>RECOMMENDATIONS FOR
          INSTITUTIONAL CAPACITY BUILDING
        </h2>
        <p style={paraStyle}>
          Institutional capacity building and training helps to highlight the
          role of all Judicial Officers, lawyers and Court officials in ensuring
          equal access to justice for all genders, abilities and age groups at
          every stage of the judicial process. To promote a user-centric design
          for District Courts, specific training sessions for institutional
          capacity building must be conducted. The following training and
          capacity building programs and initiatives will equip the Judicial
          Officers, lawyers and Court staff with the ability to manage judicial
          infrastructure, both physical and digital, to ensure that they are
          accessible, equitable, safe, comfortable, efficient, adaptable and
          sustainable for all users.
        </p>
        <h3 id="sensitisation" style={h3style}>
          <span style={{ fontSize: "20px" }}>5.1. </span>Sensitisation Programs
        </h3>
        <p style={paraStyle}>
          For each District Court, a plan of action should be put in place to
          train Advocates, Judicial Officers and staff to interact with the
          litigants and general public engaging with the justice system. This is
          necessary to ensure that they are empathetic and responsive to the
          needs of diverse users including vulnerable persons. Such programs are
          key to ensuring that the diverse users feel comfortable engaging with
          the officials and any concerns raised are adequately addressed.
        </p>
        <p style={paraStyle}>
          A plan may also be put in place to ensure that the service providers,
          police officials, etc., interacting with the diverse users, are
          adequately trained and sensitised.
        </p>
        <p style={paraStyle}>
          The following sensitisation programs may be conducted:
        </p>
        <h4 id="gender" style={h3style}>
          <span style={{ fontSize: "20px" }}>5.1.</span>A. Gender Sensitisation
        </h4>
        <p style={paraStyle}>
          It is essential to undertake training related to gender-responsive
          administration and infrastructure management to promote gender
          equality, and to ensure non-discrimination towards all users. The
          programs should seek to dismantle gender biases and create awareness
          about gender-related issues concerning all users of the Court Complex.
          Gender sensitisation programs should be conducted with an integrated
          and interdisciplinary approach. In creating the programs, the social
          and cultural nuances of all genders which shape the experiences of
          people in the local social context of the specific District Courts
          must be understood.
        </p>
        <p style={paraStyle}>
          For example, the Chandigarh High Court’s Gender Sensitisation
          Committee, has constituted a Gender Sensitisation & Internal
          Complaints Committee (“GSICC”){reference(referenceData[0], 1)} which
          conducts gender sensitization programmes and manages workplace gender
          issues to safeguard the constitutional and legal rights of women.
          {reference(referenceData[1], 2)} A similar Committee involving the
          Court Complex operations and management personnel should be formed and
          entrusted with the task of conducting workshops to sensitise staff,
          maintenance teams, and designers on gender equality concerns and
          accessibility needs of all users.
        </p>
        <DividerBorderStyle />
        <h4 id="accessibility" style={h3style}>
          <span style={{ fontSize: "20px" }}>5.1.</span>B. Accessibility
          Sensitisation
        </h4>
        <p style={paraStyle}>
          To combat the ingrained biases and misconceptions regarding
          accessibility, comprehensive training programs should be undertaken
          aimed at sensitising Court personnel, Judges, lawyers, and security
          staff about the rights and needs of Persons with Disabilities. The
          programs should aim to cultivate a more profound comprehension of the
          rights and requirements of Persons with Disabilities, highlighting the
          importance of equitable treatment and dispelling common misconceptions
          and stereotypes that obstruct their access to justice. It is also
          necessary to undertake sensitisation programs which go beyond the
          general understanding of disabilities, and take into account more
          nuanced visible and non-invisible disabilities that may affect diverse
          user groups.
        </p>
        <p style={paraStyle}>
          Furthermore, these programs should be designed to promote a culture of
          inclusivity and sensitivity within the Court system, fostering trust
          and confidence among Persons with Disabilities and ensuring seamless
          interactions with the legal system.{reference(referenceData[2], 3)}{" "}
          Adequate SoPs must be formulated under the Accessibility Plan to
          account for the training programmes (
          <span
            onClick={() =>
              goToSection(
                "accessibilityNavigationPlan",
                "headingAccessiblityPlan"
              )
            }
            style={sectionStyle}
          >
            Section 3.1
          </span>
          .)
        </p>
        <p style={paraStyle}>
          For example, sensitivity to the need for making all spaces of the
          Court Complex universally accessible, provision of unisex and
          accessible washrooms, or the need for designing the waiting areas for
          all users including provision of play areas for children must be built
          into the training programs.
        </p>
        <DividerBorderStyle />
        <h4 id="vulnerable" style={h3style}>
          <span style={{ fontSize: "20px" }}>5.1.</span>C. Vulnerable Groups
          Sensitisation
        </h4>
        <p style={paraStyle}>
          Special sensitisation sessions should be undertaken on the
          requirements of vulnerable groups and the need to design spaces for
          their comfort and easy access. Specialised medical and design experts
          should be invited to inform these designs.
        </p>
        <p style={paraStyle}>
          For example, to ensure accessible design, training sessions must
          include the following:
        </p>
        {generateUnOrderedList(DesignConsiderations)}
        <DashedBorderStyle />
        <h3 id="security" style={h3style}>
          <span style={{ fontSize: "20px" }}>5.2. </span>Security and Emergency
          Risks
        </h3>
        <p style={paraStyle}>
          To maintain security and ensure timely management of emergency risks,
          the management, staff and security personnel should undergo regular
          training and capacity building programmes to respond to disasters or
          security risks. For further details, please refer to{" "}
          <span
            onClick={() =>
              goToSection(
                "siteBuildingManagement",
                "disasterManagementStrategies"
              )
            }
            style={sectionStyle}
          >
            Section 2.6 & 4.1
          </span>
          .
        </p>
        <p style={paraStyle}>
          The training and capacity building programmes should incorporate the
          following:
        </p>
        {generateUnOrderedList(securityArray)}
        <DashedBorderStyle />
        <h3 id="operations" style={h3style}>
          <span style={{ fontSize: "20px" }}>5.3. </span>Operations and
          Management of Infrastructure
        </h3>
        <p style={paraStyle}>
          In the absence of a Facility Management Agency, the Court Managers and
          in-house maintenance staff should undergo specific training on the
          operations and management of the building services and infrastructure.
          Awareness of the procedures for operating certain Court functions and
          training for managing the building services include the following:
        </p>
        {generateUnOrderedList(infrastructureArray)}
        <h2 ref={referencesRef} id="reference" style={referenceStyle}>
          <i>References</i>
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
}

export default CapacityBuilding;
